/**
 * AsyncSurface — the canonical "alive contract" primitive.
 *
 * Operator brief (Phase 1.3, Stabilisation):
 *   "Every async operational surface must answer:
 *      • Is it loading?
 *      • Did it fail?
 *      • What can the user do next?
 *    No silent emptiness. No frozen placeholders. No dead
 *    operational states."
 *
 * This component is a thin, accessible state-machine wrapper that
 * forces every consumer to think about all three states. It is
 * deliberately minimal — no styling system, no toast wiring, no
 * navigation, no auto-retry — so it can be dropped into any
 * surface (instructor card, learner panel, lesson review, basket
 * modal) without imposing visual decisions.
 *
 * Props:
 *   • loading      – true while data is being fetched
 *   • error        – truthy when the fetch failed (Error or string)
 *   • empty        – true when the fetch succeeded but there is
 *                    nothing to show (distinct from "loading")
 *   • onRetry      – called when the user taps the retry affordance
 *                    inside the error or empty fallback
 *   • emptyLabel   – calm, surface-specific copy for the empty
 *                    state (e.g. "No lessons yet today.")
 *   • emptyAction  – optional inline action for the empty state
 *                    (label + onClick) — surfaces a next-step
 *                    affordance per doctrine
 *   • errorLabel   – calm copy for the error state (defaults to a
 *                    truthful "We couldn't load this." line)
 *   • loadingLabel – optional A11y label for the loading state
 *   • children     – the success-state subtree
 *   • testid       – data-testid stem; the loading/error/empty
 *                    surfaces are tagged "{testid}-loading",
 *                    "{testid}-error", "{testid}-empty"
 *
 * Doctrine pins:
 *   • Calm slate styling. No red panic.
 *   • Truthful — never hides errors behind a fake "loading"
 *     state; never pretends empty is success.
 *   • Always offers a next step (retry, or a caller-provided
 *     emptyAction).
 */
import React from "react";
import { AlertTriangle, Inbox, Loader2 } from "lucide-react";

const DEFAULT_ERROR_LABEL = "We couldn't load this.";

function LoadingState({ testid, label }) {
  return (
    <div
      data-testid={`${testid}-loading`}
      data-async-state="loading"
      role="status"
      aria-live="polite"
      className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4 flex items-center gap-3"
    >
      <Loader2 className="w-4 h-4 text-slate-500 animate-spin" />
      <span className="text-sm text-slate-600 dark:text-slate-300">
        {label || "Loading…"}
      </span>
    </div>
  );
}

function ErrorState({ testid, label, onRetry }) {
  return (
    <div
      data-testid={`${testid}-error`}
      data-async-state="error"
      role="alert"
      className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/60 p-4"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-700 dark:text-slate-200">
            {label || DEFAULT_ERROR_LABEL}
          </p>
          {onRetry && (
            <button
              data-testid={`${testid}-error-retry`}
              onClick={onRetry}
              className="mt-2 text-xs font-bold text-slate-700 dark:text-slate-200 underline underline-offset-2"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ testid, label, action, onRetry }) {
  return (
    <div
      data-testid={`${testid}-empty`}
      data-async-state="empty"
      className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 bg-transparent p-4"
    >
      <div className="flex items-start gap-3">
        <Inbox className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {label || "Nothing here yet."}
          </p>
          {action && action.onClick && action.label && (
            <button
              data-testid={`${testid}-empty-action`}
              onClick={action.onClick}
              className="mt-2 text-xs font-bold text-slate-700 dark:text-slate-200 underline underline-offset-2"
            >
              {action.label}
            </button>
          )}
          {!action && onRetry && (
            <button
              data-testid={`${testid}-empty-retry`}
              onClick={onRetry}
              className="mt-2 text-xs font-bold text-slate-700 dark:text-slate-200 underline underline-offset-2"
            >
              Refresh
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Single primitive. Resolution order:
 *   1. loading      → LoadingState
 *   2. error        → ErrorState (retry affordance if onRetry given)
 *   3. empty        → EmptyState (action or retry affordance)
 *   4. otherwise    → children
 *
 * Keeping the resolution order explicit prevents the silent
 * "loading=true && error=truthy" race condition from rendering the
 * spinner forever on top of an actual failure.
 */
export default function AsyncSurface({
  loading,
  error,
  empty,
  onRetry,
  emptyLabel,
  emptyAction,
  errorLabel,
  loadingLabel,
  children,
  testid = "async-surface",
}) {
  if (loading) {
    return <LoadingState testid={testid} label={loadingLabel} />;
  }
  if (error) {
    const label = errorLabel || (typeof error === "string" ? error : DEFAULT_ERROR_LABEL);
    return <ErrorState testid={testid} label={label} onRetry={onRetry} />;
  }
  if (empty) {
    return (
      <EmptyState
        testid={testid}
        label={emptyLabel}
        action={emptyAction}
        onRetry={onRetry}
      />
    );
  }
  return <>{children}</>;
}
